/**
 * Trade API client methods.
 *
 * T060 [US3]: Create tradesApi methods for manual trade CRUD.
 * Handles creating, reading, updating, and deleting trades.
 */

import { API_CONFIG } from '../config/api';
import { getToken } from './tokenStorage';
import { logApiRequest, logApiResponse, logApiError } from '../utils/errorHandler';

export type TransactionType =
  | 'buy'
  | 'sell'
  | 'deposit'
  | 'withdrawal'
  | 'split'
  | 'consolidate'
  | 'bonus';

export type CorporateActionType = 'split' | 'consolidate' | 'bonus';

export type SplitConsolidateType = 'split' | 'consolidate';

export interface CreateSplitConsolidateRequest {
  position_id: number;
  action_type: SplitConsolidateType;
  from_shares: number;
  to_shares: number;
  exclude_date: string; // ISO datetime format - trades ON or AFTER this date are NOT affected
  description?: string;
}

export interface SplitConsolidateResponse {
  id: number;
  user_id: number;
  position_id: number;
  symbol: string; // Included for display purposes (from position)
  action_type: string;
  from_shares: number;
  to_shares: number;
  ratio: number;
  exclude_date: string;
  description: string;
  trades_updated: number;
  created_at: string;
  updated_at?: string;
}

export interface CreateTradeRequest {
  symbol: string;
  trade_datetime: string; // ISO datetime format
  quantity: number;
  trade_price: number;
  currency: string;
  transaction_type: TransactionType;
  comment?: string;
}

export interface CorporateActionRequest {
  position_id: number;
  action_type: CorporateActionType;
  from_shares: number;
  to_shares: number;
  exclude_date: string; // YYYY-MM-DD format (ISO datetime)
  description?: string;
}

export interface CorporateActionResponse {
  id: number;
  symbol: string;
  action_type: string;
  ratio: number;
  exclude_date: string;
  description: string;
  trades_updated: number;
  created_at: string;
}

export interface UpdateTradeRequest {
  quantity?: number;
  trade_price?: number;
  is_short_sell?: boolean;
  is_cover_short?: boolean;
  transaction_type?: TransactionType;
  comment?: string;
}

/**
 * Create a new trade manually.
 * T060 [US3]: Create createTrade method.
 */
export async function createTrade(tradeData: CreateTradeRequest): Promise<Trade> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/trades`;
  const method = 'POST';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url, tradeData);

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(tradeData),
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.detail || 'Failed to create trade');
      logApiError(method, url, error, data);
      throw error;
    }

    logApiResponse(method, url, data, startTime);
    return data;
  } catch (error) {
    logApiError(method, url, error);
    throw error;
  }
}

/**
 * Delete a corporate action trade.
 * This will reverse the effects of the corporate action on all affected trades.
 */
export async function deleteCorporateAction(tradeId: number): Promise<void> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/trades/${tradeId}`;
  const method = 'DELETE';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url, { tradeId });

    const response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        const error = new Error('Corporate action not found');
        logApiError(method, url, error);
        throw error;
      }
      const data = await response.json();
      const error = new Error(data.detail || 'Failed to delete corporate action');
      logApiError(method, url, error, data);
      throw error;
    }

    logApiResponse(method, url, null, startTime);
  } catch (error) {
    logApiError(method, url, error);
    throw error;
  }
}

/**
 * Get a specific trade by ID.
 * T060 [US3]: Create getTrade method.
 */
export async function getTrade(tradeId: number): Promise<Trade> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/trades/${tradeId}`;
  const method = 'GET';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url);

    const response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.detail || 'Failed to get trade');
      logApiError(method, url, error, data);
      throw error;
    }

    logApiResponse(method, url, data, startTime);
    return data;
  } catch (error) {
    logApiError(method, url, error);
    throw error;
  }
}

/**
 * Update an existing trade.
 * T060 [US3]: Create updateTrade method.
 */
export async function updateTrade(tradeId: number, tradeData: UpdateTradeRequest): Promise<Trade> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/trades/${tradeId}`;
  const method = 'PUT';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url, tradeData);

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(tradeData),
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.detail || 'Failed to update trade');
      logApiError(method, url, error, data);
      throw error;
    }

    logApiResponse(method, url, data, startTime);
    return data;
  } catch (error) {
    logApiError(method, url, error);
    throw error;
  }
}

/**
 * Delete a trade.
 * T060 [US3]: Create deleteTrade method.
 */
export async function deleteTrade(tradeId: number): Promise<void> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/trades/${tradeId}`;
  const method = 'DELETE';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url);

    const response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const data = await response.json();
      const error = new Error(data.detail || 'Failed to delete trade');
      logApiError(method, url, error, data);
      throw error;
    }

    logApiResponse(method, url, null, startTime);
  } catch (error) {
    logApiError(method, url, error);
    throw error;
  }
}

/**
 * Create a corporate action (share split, consolidation, or bonus shares).
 * Updates all trades BEFORE the exclude date (trades ON or AFTER are NOT affected).
 *
 * New API format - uses position_id, from_shares, to_shares, exclude_date
 */
export async function createCorporateAction(
  caData: CorporateActionRequest
): Promise<CorporateActionResponse> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/trades/corporate-action`;
  const method = 'POST';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url, caData);

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        position_id: caData.position_id,
        action_type: caData.action_type,
        from_shares: caData.from_shares,
        to_shares: caData.to_shares,
        exclude_date: caData.exclude_date,
        description: caData.description,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      // Parse error detail for better error messages
      let errorMessage = 'Failed to create corporate action';
      if (data.detail) {
        if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (Array.isArray(data.detail)) {
          // Handle validation errors
          errorMessage = data.detail.map((e: any) => e.msg || e.message || String(e)).join(', ');
        } else if (typeof data.detail === 'object') {
          errorMessage = JSON.stringify(data.detail);
        }
      }
      const error = new Error(errorMessage);
      logApiError(method, url, error, data);
      throw error;
    }

    logApiResponse(method, url, data, startTime);
    return data;
  } catch (error) {
    logApiError(method, url, error);
    throw error;
  }
}

/**
 * Get corporate actions for a position.
 * Returns all corporate actions for the user, optionally filtered by position_id.
 */
export async function getCorporateActions(
  positionId?: number
): Promise<SplitConsolidateResponse[]> {
  const startTime = Date.now();
  const params = positionId ? `?position_id=${positionId}` : '';
  const url = `${API_CONFIG.baseURL}/api/v1/trades/corporate-actions${params}`;
  const method = 'GET';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url, { positionId });

    const response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.detail || 'Failed to fetch corporate actions');
      logApiError(method, url, error, data);
      throw error;
    }

    logApiResponse(method, url, data, startTime);
    return data;
  } catch (error) {
    logApiError(method, url, error);
    throw error;
  }
}

/**
 * Delete a corporate action by ID (soft delete - marks is_deleted=true).
 */
export async function deleteCorporateActionById(caId: number): Promise<void> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/trades/corporate-action/${caId}`;
  const method = 'DELETE';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url, { caId });

    const response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        const error = new Error('Corporate action not found');
        logApiError(method, url, error);
        throw error;
      }
      const data = await response.json();
      const error = new Error(data.detail || 'Failed to delete corporate action');
      logApiError(method, url, error, data);
      throw error;
    }

    logApiResponse(method, url, null, startTime);
  } catch (error) {
    logApiError(method, url, error);
    throw error;
  }
}

/**
 * Update a corporate action description.
 *
 * @param caId - Corporate action ID
 * @param description - New description text
 * @returns Updated corporate action
 */
export async function updateCorporateActionDescription(
  caId: number,
  description: string
): Promise<SplitConsolidateResponse> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/trades/corporate-action/${caId}`;
  const method = 'PUT';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url, { caId, description });

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ description }),
    });

    if (!response.ok) {
      const data = await response.json();
      const error = new Error(data.detail || 'Failed to update corporate action');
      logApiError(method, url, error, data);
      throw error;
    }

    const data = await response.json();
    logApiResponse(method, url, data, startTime);
    return data;
  } catch (error) {
    logApiError(method, url, error);
    throw error;
  }
}

// ==================== Split/Consolidate Corporate Actions (Separate Table) ====================

/**
 * Create a split or consolidation corporate action (stored in separate table).
 * Uses explicit from_shares and to_shares for accurate reversal.
 *
 * Examples:
 * - 2-for-1 split: from_shares=1, to_shares=2
 * - 3-for-2 split: from_shares=2, to_shares=3
 * - 1-for-10 consolidation: from_shares=10, to_shares=1
 */
export async function createSplitConsolidateCA(
  caData: CreateSplitConsolidateRequest
): Promise<SplitConsolidateResponse> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/trades/split-consolidate`;
  const method = 'POST';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url, caData);

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(caData),
    });

    const data = await response.json();

    if (!response.ok) {
      let errorMessage = 'Failed to create corporate action';
      if (data.detail) {
        if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map((e: any) => e.msg || e.message || String(e)).join(', ');
        } else if (typeof data.detail === 'object') {
          errorMessage = JSON.stringify(data.detail);
        }
      }
      const error = new Error(errorMessage);
      logApiError(method, url, error, data);
      throw error;
    }

    logApiResponse(method, url, data, startTime);
    return data;
  } catch (error) {
    logApiError(method, url, error);
    throw error;
  }
}

/**
 * Delete a split or consolidation corporate action and reverse its effects.
 */
export async function deleteSplitConsolidateCA(caId: number): Promise<void> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/trades/split-consolidate/${caId}`;
  const method = 'DELETE';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url, { caId });

    const response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        const error = new Error('Corporate action not found');
        logApiError(method, url, error);
        throw error;
      }
      const data = await response.json();
      const error = new Error(data.detail || 'Failed to delete corporate action');
      logApiError(method, url, error, data);
      throw error;
    }

    logApiResponse(method, url, null, startTime);
  } catch (error) {
    logApiError(method, url, error);
    throw error;
  }
}

/**
 * Get all split and consolidation corporate actions for the user.
 */
export async function getSplitConsolidateCAs(
  positionId?: number
): Promise<SplitConsolidateResponse[]> {
  const startTime = Date.now();
  const url = new URL(`${API_CONFIG.baseURL}/api/v1/trades/split-consolidate`);
  if (positionId) {
    url.searchParams.append('position_id', positionId.toString());
  }
  const method = 'GET';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url.toString(), new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url.toString());

    const response = await fetch(url.toString(), {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.detail || 'Failed to get corporate actions');
      logApiError(method, url.toString(), error, data);
      throw error;
    }

    logApiResponse(method, url.toString(), data, startTime);
    return data;
  } catch (error) {
    logApiError(method, url.toString(), error);
    throw error;
  }
}

/**
 * Get a specific split or consolidation corporate action by ID.
 */
export async function getSplitConsolidateCA(caId: number): Promise<SplitConsolidateResponse> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/trades/split-consolidate/${caId}`;
  const method = 'GET';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url);

    const response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 404) {
        const error = new Error('Corporate action not found');
        logApiError(method, url, error);
        throw error;
      }
      const error = new Error(data.detail || 'Failed to get corporate action');
      logApiError(method, url, error, data);
      throw error;
    }

    logApiResponse(method, url, data, startTime);
    return data;
  } catch (error) {
    logApiError(method, url, error);
    throw error;
  }
}
